import { useApplicantAPI } from "@certn/api";
import { BasicButton, Stack, Text } from "@certn/cascadia";
import {
  Card,
  Container,
  Illustration,
  Layout,
  Loader,
} from "@certn/cascadia-react";
import { ProgressStepper } from "@certn/cascadia-react/components/stepper/ProgressStepper";
import { css } from "@emotion/css";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { ApplicationSubmittedContinueInApp } from "./ApplicationSubmittedContinueInApp";
import { buildSubmittedScreenTimelineSteps } from "../dashboard/history-card/submittedTimeline";
import { AppFooter } from "../../components/app-footer";
import { AppHeader } from "../../components/app-header";
import { useDocumentTitle } from "../../hooks/api/useDocumentTitle";
import { ReferAndEarn } from "../../components/referral/ReferAndEarn";
import { isApprovedReferralCaseSource } from "../../components/referral/referralShare";

export function ApplicationSubmitted() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { vcnClientPortalIntegration, applicantPortalSubmittedTimeline } =
    useFlags();

  const { caseId } = useParams();
  const { data, isLoading } = useApplicantAPI(
    `/api/internal/applicant/case/${caseId}/credential-request/`
  );
  const showContinueInApp = Boolean(vcnClientPortalIntegration && data);

  useDocumentTitle(
    isLoading || showContinueInApp
      ? null
      : t("Background check submitted — Certn")
  );
  const { data: caseData } = useApplicantAPI(
    caseId
      ? (`/api/internal/applicant/case/${caseId}/` as "/api/internal/applicant/case/{id}/")
      : null
  );
  const submittedTimelineSteps = applicantPortalSubmittedTimeline
    ? buildSubmittedScreenTimelineSteps(t)
    : [];

  if (isLoading) {
    return (
      <Layout header={<AppHeader />} footer={<AppFooter />}>
        <Loader overlay />
      </Layout>
    );
  }

  if (vcnClientPortalIntegration && data) {
    return <ApplicationSubmittedContinueInApp credentialRequest={data} />;
  }

  return (
    <Layout
      layout="applicant/narrow"
      header={<AppHeader />}
      footer={<AppFooter />}
      backgroundColor="surface-sunken"
    >
      <Container>
        <Stack align="center" gap="space-6" className={styles.submitted}>
          <Text as="h1" style="form/page-title" align="center">
            {t("Background check submitted")}
          </Text>
          <Illustration name="success" />
          <Text style="form/body-primary" align="center" color="text-weak">
            {t(
              "Your information has been submitted, and you will be notified when your checks are complete."
            )}
          </Text>
          {applicantPortalSubmittedTimeline &&
            submittedTimelineSteps.length > 0 && (
              <Card padding="space-4" className={styles.timelineCard}>
                <Stack gap="space-4">
                  <Text style="global/heading-medium">
                    {t("What happens next")}
                  </Text>
                  <div>
                    <ProgressStepper steps={submittedTimelineSteps} />
                  </div>
                </Stack>
              </Card>
            )}
          {isApprovedReferralCaseSource(caseData?.source) && <ReferAndEarn />}
          <BasicButton fullWidth size="large" onPress={() => navigate("/")}>
            {t("Go to dashboard")}
          </BasicButton>
        </Stack>
      </Container>
    </Layout>
  );
}

const styles = {
  submitted: css({ paddingTop: "var(--space-6)" }),
  // Card's padding prop (space-4 = 16px per side) is added outside width because
  // box-sizing defaults to content-box (no global border-box reset in this app).
  // Without border-box: rendered width = 100% (560px) + 32px padding = 592px, overflowing the container.
  timelineCard: css({ width: "100%", boxSizing: "border-box" }),
};
